import { format } from 'date-fns';
import { de } from 'date-fns/locale';
import { type ReactNode } from 'react';
import { FormattedMessage } from 'react-intl';
import { toast } from 'react-toastify';
import { graphqlErrorsOf } from './error';
import requireValue from './requireValue';

const DEFAULT_LOCALE = 'de-DE';

/**
 * The message with this id. A few callers pass a message descriptor (`__({ id })`) instead of the id; react-intl
 * formats the id as a string, so those render "[object Object]".
 */
export function __(id: string | object, values?: Record<string, ReactNode>) {
  // Bug: `__({ id: '...' })` callers get the "[object Object]" message instead of their text; kept as it was.
  return <FormattedMessage id={String(id)} values={values} />;
}

export const wait = (timeout = 3000) =>
  new Promise((resolve) => {
    setTimeout(resolve, timeout);
  });

function getLocale() {
  return localStorage.getItem('language') || DEFAULT_LOCALE;
}

export const value2LocaleValue = (value: number | null) => {
  // const config = { de: 'de-DE', en: 'en-GB' };
  const locale = getLocale();
  // Intl formats null as 0, as Number(null) is.
  return Intl.NumberFormat(locale).format(Number(value));
};

export const formatCurrency = (() => {
  const locale = getLocale();
  const formatter = new Intl.NumberFormat(locale, { style: 'currency', currency: 'EUR' });
  // Intl converts the value to a number as Number() does (null is 0).
  return (number: number | null | undefined) => {
    const parts = formatter.formatToParts(Number(number));
    const strArray: string[] = [];
    // filter out the decimal part
    parts.forEach((part) => {
      if (['decimal', 'fraction'].includes(part.type)) return;
      strArray.push(part.value);
    });
    return strArray.join('');
  };
})();

/** A date value of the API or a form; null and undefined are what `new Date(null)` and `new Date(undefined)` make. */
export type TDateLike = string | number | Date | null | undefined;

/**
 * `new Date(value)` for every value the untyped code passed: null is the epoch, undefined an invalid date, a
 * boolean the timestamp 0 or 1, any other value its string.
 */
export const toDate = (value: unknown): Date => {
  if (value === null) return new Date(0);
  if (value === undefined) return new Date(NaN);
  if (typeof value === 'string' || typeof value === 'number' || value instanceof Date)
    return new Date(value);
  if (typeof value === 'boolean') return new Date(Number(value));
  return new Date(String(value));
};

export const formatDate = (date: unknown, dateFormat = 'do MMMM yyyy HH:mm') => {
  return format(toDate(date), dateFormat, { locale: de });
};

export const changeLanguage = () => {
  let countClicks: string | number | null | undefined =
    window?.localStorage?.getItem('countClicksOnFooter');

  if (Number(countClicks) === 5) {
    countClicks = 0;

    const language = window?.localStorage?.getItem('language');
    window?.localStorage?.setItem('language', language === 'en' ? 'de' : 'en');

    document.location.reload();
  }

  if (!countClicks) window?.localStorage?.setItem('countClicksOnFooter', String(1));
  else window?.localStorage?.setItem('countClicksOnFooter', String(Number(countClicks) + 1));
};

/** An Error (or the caught value) of a failed operation, with the original value in `raw`. */
export interface IErrorWithRaw {
  message?: unknown;
  raw: unknown;
}

/**
 * An Error with the messages of the GraphQL errors joined by commas (what `new Error([messages])` gave for the
 * ApolloError of Apollo Client 2), or the caught value itself; both carry the caught value in `raw`. Apollo Client 4
 * rejects with CombinedGraphQLErrors for GraphQL errors; its other operation errors (no connection, HTTP status,
 * unreadable body, a link's error) were an ApolloError with an empty graphQLErrors list, hence the empty message.
 */
export const formatGraphqlErrors = (error: unknown): IErrorWithRaw => {
  // Reading `error.graphQLErrors` throws for null and undefined.
  requireValue(error, 'graphQLErrors');
  const graphQLErrors = graphqlErrorsOf(error);
  if (graphQLErrors) {
    const messages = graphQLErrors.map((err) => err.message);
    return Object.assign(new Error(String(messages)), { raw: error || '' });
  }
  if (typeof error !== 'object' || error === null) {
    throw new TypeError(`Cannot create property 'raw' on ${typeof error} '${String(error)}'`);
  }
  return Object.assign(error, { raw: error || '' });
};

/** The message of a value of formatGraphqlErrors: a string for errors, undefined for other caught values. */
const messageOf = (error: IErrorWithRaw): string | undefined => {
  if (typeof error.message === 'string') return error.message;
  return undefined;
};

export const grabErrorMessage = (error: unknown) => messageOf(formatGraphqlErrors(error));

export const toastErrorMessage = (error: unknown) => {
  const e = formatGraphqlErrors(error);
  toast.error(messageOf(e));
};
